import { plainToClass } from 'class-transformer';
import { UtilityHelper } from '../../utility.helper';
import { FormTemplateInstancesButtonInfo } from './form-template-instances-button-info';

export class FormTemplateInstancesButtons {
    private formTemplateInstancesButtonInfo: FormTemplateInstancesButtonInfo[] = null;

    // Constants.
    private static readonly NewForm_DisplayName: string = 'New Form';
    private static readonly NewFolder_DisplayName: string = 'New Folder';
    private static readonly DeleteForms_DisplayName: string = 'Delete Form(s)';
    private static readonly MoveForms_DisplayName: string = 'Move Form(s)';
    private static readonly CopyForms_DisplayName: string = 'Copy Form(s)';
    private static readonly PublishForm_DisplayName: string = 'Publish Form(s)';
    private static readonly PublishFolder_DisplayName: string = 'Publish Folder';
    private static readonly ExportData_DisplayName: string = 'Export Data';
    private static readonly BulkWorkflow_DisplayName: string = 'Bulk Workflow';

    private static readonly buttonNames: string[] =
        [
            FormTemplateInstancesButtons.NewForm_DisplayName,
            FormTemplateInstancesButtons.NewFolder_DisplayName,
            FormTemplateInstancesButtons.DeleteForms_DisplayName,
            FormTemplateInstancesButtons.MoveForms_DisplayName,
            FormTemplateInstancesButtons.CopyForms_DisplayName,
            FormTemplateInstancesButtons.PublishForm_DisplayName,
            FormTemplateInstancesButtons.PublishFolder_DisplayName,
            FormTemplateInstancesButtons.ExportData_DisplayName,
            FormTemplateInstancesButtons.BulkWorkflow_DisplayName
        ];

    public static readonly simpleFormGridButtonNames: string[] =
        [
            FormTemplateInstancesButtons.DeleteForms_DisplayName,
            FormTemplateInstancesButtons.MoveForms_DisplayName,
            FormTemplateInstancesButtons.PublishForm_DisplayName,
            FormTemplateInstancesButtons.PublishFolder_DisplayName,
            FormTemplateInstancesButtons.ExportData_DisplayName
        ];

    private readonly shortNameToDisplayName: { [shortName: string]: string } = {
        'createform': FormTemplateInstancesButtons.NewForm_DisplayName,
        'createfolder': FormTemplateInstancesButtons.NewFolder_DisplayName,
        'delete': FormTemplateInstancesButtons.DeleteForms_DisplayName,
        'move': FormTemplateInstancesButtons.MoveForms_DisplayName,
        'copy': FormTemplateInstancesButtons.CopyForms_DisplayName,
        'publishform': FormTemplateInstancesButtons.PublishForm_DisplayName,
        'publishfolder': FormTemplateInstancesButtons.PublishFolder_DisplayName,
        'exportData': FormTemplateInstancesButtons.ExportData_DisplayName,
        'bulkWorkflow': FormTemplateInstancesButtons.BulkWorkflow_DisplayName
    }

    public get FormTemplateInstancesButtonInfo(): FormTemplateInstancesButtonInfo[] {
        return this.formTemplateInstancesButtonInfo;
    }
    public set FormTemplateInstancesButtonInfo(value: FormTemplateInstancesButtonInfo[] ) {
        this.formTemplateInstancesButtonInfo = value;
    }

    // Returns the display name for the given button 
    public getButtonName(formTemplateInstancesButtonName: string): string {
        let buttonDisplayName: string = this.shortNameToDisplayName[formTemplateInstancesButtonName];
        return this.doGetButtonName(buttonDisplayName);
    }

    // returns whether or not the button should be displayed
    public buttonIsEnabled(formTemplateInstancesButtonName: string): boolean {
        let buttonEnabled: boolean = false;

        let buttonDisplayName: string = this.shortNameToDisplayName[formTemplateInstancesButtonName];

        if (buttonDisplayName != null) {
            buttonEnabled = this.doGetButtonEnabled(buttonDisplayName);
        }

        return buttonEnabled;
    }

    /*
        Prior to work on VNEXT-1031 a pipe delimited list of buttons to hide was saved to FormField.formTemplateInstanceActionButtonsConfig
        For VNEXT-1031 the field needs to store JSON so that it can store additional configuration for each button
        The method is therefore self-correcting of the earlier way the config was stored.
    */
    public createButtonInfoFromSavedConfig(formTemplateInstanceActionButtonsConfig: string): void {
        if (formTemplateInstanceActionButtonsConfig) {
            let parsedJson = UtilityHelper.tryParseJson(formTemplateInstanceActionButtonsConfig);
            if (parsedJson) {
                let parsedJson = JSON.parse(formTemplateInstanceActionButtonsConfig);
                let obj = plainToClass(FormTemplateInstancesButtons, parsedJson);
                this.formTemplateInstancesButtonInfo = obj.formTemplateInstancesButtonInfo;
            } else {
                // Convert old pipe delimited structure to new JSON structure
                let configuredButtonNames: string[] = formTemplateInstanceActionButtonsConfig.split('|');
                let matchingButtonNames: string[] = FormTemplateInstancesButtons.buttonNames.filter(bn => configuredButtonNames.includes(bn));

                if (matchingButtonNames.length > 0) {
                    this.formTemplateInstancesButtonInfo = [];

                    for (let index: number = 0; index < FormTemplateInstancesButtons.buttonNames.length; index++) {
                        let buttonName = FormTemplateInstancesButtons.buttonNames[index];
                        let showButton: boolean = !configuredButtonNames.includes(buttonName);
                        let buttonInfo: FormTemplateInstancesButtonInfo = new FormTemplateInstancesButtonInfo(buttonName, showButton);
                        this.formTemplateInstancesButtonInfo.push(buttonInfo);
                    }
                } else {
                    // Create a default configuration.
                    this.createDefaultButtonInfoIfNoConfigExists();
                }
            }
        }
    }

    /*
        Prior to work on VNEXT-1031 a pipe delimited list of buttons to hide was saved to FormField.formTemplateInstanceActionButtonsConfig
        For VNEXT-1031 the field needs to store JSON so that it can store additional configuration for each button
    */
    public toFormFieldPropertyValue(): string {
        // filter out properties we don't want/need to store in the JSON representation of this object
        const propertyFilter = (key, value) => {
            if (key === "shortNameToDisplayName") return undefined;
            return value;
        }
        let jsonString = JSON.stringify(this, propertyFilter);
        return jsonString;
    }

    public createDefaultButtonInfoIfNoConfigExists(): void {
        //debugger;
        if (this.formTemplateInstancesButtonInfo == null) {
            this.formTemplateInstancesButtonInfo = [];

            for (let index: number = 0; index < FormTemplateInstancesButtons.buttonNames.length; index++) {
                let buttonInfo: FormTemplateInstancesButtonInfo = new FormTemplateInstancesButtonInfo(FormTemplateInstancesButtons.buttonNames[index]);
                this.formTemplateInstancesButtonInfo.push(buttonInfo);
            }
        }
    }

    // Helper methods.
    private doGetButtonEnabled(buttonDisplayName: string): boolean {
        let buttonEnabled: boolean = false;

        if (this.formTemplateInstancesButtonInfo != null) {
            let buttonInfo: FormTemplateInstancesButtonInfo = this.formTemplateInstancesButtonInfo.find(bi => bi.buttonDisplayName == buttonDisplayName);
            buttonEnabled = (buttonInfo != null) && buttonInfo.enableButton;
        }

        return buttonEnabled;
    }

    // If an alias has been defined, return that, otherwise return the original display name
    private doGetButtonName(buttonDisplayName: string): string {
        let name: string = buttonDisplayName;

        if (this.formTemplateInstancesButtonInfo != null) {
            let buttonInfo: FormTemplateInstancesButtonInfo = this.formTemplateInstancesButtonInfo.find(bi => bi.buttonDisplayName == buttonDisplayName);
            if (buttonInfo?.alias) {
                name = buttonInfo.alias;
            }
        }
        return name;
    }
}
